/**
 * CreateFromPublicationWizard
 * Intelligent wizard that classifies publications and creates appropriate entities
 * - Detects if publication should become Event or Task
 * - Pre-fills forms with extracted data
 * - Allows user to override classification
 */

import { useState, useEffect, type ComponentType } from 'react'
import { AlertCircle, ArrowLeft, Calendar, CheckSquare, ChevronRight, FileText, Info } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/utils/cn'
import { DJENPublication } from '@/types/publication'
import { classifyPublication, PublicationClassification } from '@/utils/publication-classifier'
import { CreateEventFromPublicationForm } from './CreateEventFromPublicationForm'
import { CreateCaseFromPublicationForm } from './CreateCaseFromPublicationForm'
import { toast } from 'sonner'

interface CreateFromPublicationWizardProps {
  publication: DJENPublication
  onSuccess: () => void
  onCancel: () => void
  /** Tarefa: abre a gaveta única (pré-preenchida com a publicação). */
  onCreateTask: () => void
}

type CreationType = 'case' | 'event' | 'task'

const CONFIDENCE_LABEL: Record<PublicationClassification['confidence'], string> = {
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
}

const CONFIDENCE_VARIANT: Record<PublicationClassification['confidence'], 'default' | 'secondary' | 'outline'> = {
  high: 'default',
  medium: 'secondary',
  low: 'outline',
}

interface CreationOptionProps {
  icon: ComponentType<{ className?: string }>
  title: string
  description: string
  selected: boolean
  suggested?: boolean
  onSelect: () => void
}

/** Cartão de escolha do que criar; marca a opção selecionada e a sugerida pela classificação. */
function CreationOption({ icon: Icon, title, description, selected, suggested = false, onSelect }: CreationOptionProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'relative flex min-w-0 flex-col rounded-lg border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
        suggested && !selected && 'ring-2 ring-primary/20',
      )}
    >
      {suggested && (
        <Badge className="absolute -right-2 -top-2">Sugerido</Badge>
      )}
      {selected && (
        <CheckSquare className="absolute right-2 top-2 size-5 text-primary" aria-hidden="true" />
      )}
      <Icon className={cn('mb-3 size-8', selected ? 'text-primary' : 'text-muted-foreground')} />
      <h4 className="mb-1 font-semibold">{title}</h4>
      <p className="text-sm text-muted-foreground">{description}</p>
    </button>
  )
}

export function CreateFromPublicationWizard({
  publication,
  onSuccess,
  onCancel,
  onCreateTask
}: CreateFromPublicationWizardProps) {
  const [classification, setClassification] = useState<PublicationClassification | null>(null)
  const [selectedType, setSelectedType] = useState<CreationType | null>(null)
  const [showForm, setShowForm] = useState(false)

  useEffect(() => {
    // Classify publication automatically
    const result = classifyPublication(publication.texto)
    setClassification(result)

    // Pre-select based on classification
    if (result.type === 'event') {
      setSelectedType('event')
    } else {
      setSelectedType('task')
    }

  }, [publication])

  const handleTypeSelect = (type: CreationType) => {
    setSelectedType(type)
  }

  const handleContinue = () => {
    if (!selectedType) {
      toast.error('Selecione o tipo de criação')
      return
    }
    if (selectedType === 'task') {
      onCreateTask()
      return
    }
    setShowForm(true)
  }

  const handleFormSuccess = () => {
    toast.success(
      selectedType === 'case' ? 'Processo criado com sucesso!' :
      selectedType === 'event' ? 'Evento criado com sucesso!' :
      'Tarefa criada com sucesso!'
    )
    onSuccess()
  }

  if (!classification) {
    return (
      <div className="flex flex-col gap-3 p-4" role="status">
        <span className="sr-only">Classificando publicação...</span>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  // If form is shown, render the appropriate form
  if (showForm && selectedType) {
    return (
      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowForm(false)}
          className="-ml-2 mb-4"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Voltar para seleção
        </Button>

        {selectedType === 'case' && (
          <CreateCaseFromPublicationForm
            publication={publication}
            onSuccess={handleFormSuccess}
            onCancel={onCancel}
          />
        )}

        {selectedType === 'event' && (
          <CreateEventFromPublicationForm
            publication={publication}
            classification={classification}
            onSuccess={handleFormSuccess}
            onCancel={onCancel}
          />
        )}

      </div>
    )
  }

  // Otherwise, show the wizard/selection screen
  return (
    <div className="flex flex-col gap-6">
      {/* AI Classification Result */}
      <div className={cn('rounded-lg border p-4', classification.confidence === 'high' && 'border-l-4 border-l-primary')}>
        <div className="flex items-start gap-3">
          <AlertCircle
            className={cn('mt-0.5 size-5 shrink-0', classification.confidence === 'high' ? 'text-primary' : 'text-muted-foreground')}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <h4 className="mb-1 flex flex-wrap items-center gap-2 font-semibold">
              Classificação Automática
              <Badge variant={CONFIDENCE_VARIANT[classification.confidence]}>
                Confiança: {CONFIDENCE_LABEL[classification.confidence]}
              </Badge>
            </h4>
            <p className="mb-2 text-sm text-muted-foreground">
              Esta publicação foi classificada como <strong className="text-foreground">{classification.type === 'event' ? 'EVENTO' : 'TAREFA'}</strong>
              {classification.suggestedEventType && ` (${classification.suggestedEventType})`}
              {classification.suggestedTaskCategory && ` (${classification.suggestedTaskCategory})`}
            </p>

            {/* Classification Reasoning */}
            {classification.reasoning.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                  Ver raciocínio da classificação
                </summary>
                <ul className="mt-2 list-disc space-y-1 rounded-md bg-muted/40 p-2 pl-6 text-xs text-muted-foreground">
                  {classification.reasoning.map((reason, index) => (
                    <li key={index}>{reason}</li>
                  ))}
                </ul>
              </details>
            )}

            {/* Extracted Data Preview */}
            {Object.keys(classification.extractedData).length > 0 && (
              <div className="mt-3 text-sm">
                <div className="mb-1 font-medium">Dados extraídos:</div>
                <div className="grid grid-cols-1 gap-2 break-words text-xs sm:grid-cols-2">
                  {classification.extractedData.date && (
                    <div>
                      <span className="text-muted-foreground">Data:</span>{' '}
                      <span className="font-medium">{new Date(classification.extractedData.date).toLocaleDateString('pt-BR')}</span>
                    </div>
                  )}
                  {classification.extractedData.startTime && (
                    <div>
                      <span className="text-muted-foreground">Horário:</span>{' '}
                      <span className="font-medium">{classification.extractedData.startTime}</span>
                    </div>
                  )}
                  {classification.extractedData.location && (
                    <div>
                      <span className="text-muted-foreground">Local:</span>{' '}
                      <span className="font-medium">{classification.extractedData.location}</span>
                    </div>
                  )}
                  {classification.extractedData.deadline && (
                    <div>
                      <span className="text-muted-foreground">Prazo:</span>{' '}
                      <span className="font-medium">{classification.extractedData.deadline} dias</span>
                    </div>
                  )}
                  {classification.extractedData.hearingType && (
                    <div>
                      <span className="text-muted-foreground">Tipo:</span>{' '}
                      <span className="font-medium">{classification.extractedData.hearingType}</span>
                    </div>
                  )}
                  {classification.extractedData.courtName && (
                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground">Tribunal:</span>{' '}
                      <span className="font-medium">{classification.extractedData.courtName}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Type Selection */}
      <div>
        <h3 className="mb-3 text-lg font-semibold">
          O que deseja criar a partir desta publicação?
        </h3>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <CreationOption
            icon={FileText}
            title="Novo Processo"
            description="Criar um novo processo judicial a partir desta publicação"
            selected={selectedType === 'case'}
            onSelect={() => handleTypeSelect('case')}
          />
          <CreationOption
            icon={Calendar}
            title="Evento Agendado"
            description="Criar um evento (audiência, reunião, compromisso)"
            selected={selectedType === 'event'}
            suggested={classification.type === 'event'}
            onSelect={() => handleTypeSelect('event')}
          />
          <CreationOption
            icon={CheckSquare}
            title="Tarefa/Prazo"
            description="Criar uma tarefa ou prazo processual"
            selected={selectedType === 'task'}
            suggested={classification.type === 'task'}
            onSelect={() => handleTypeSelect('task')}
          />
        </div>
      </div>

      {/* Info Box */}
      <div role="note" className="rounded-lg border bg-muted/40 p-3">
        <div className="flex gap-2">
          <Info className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Dica:</strong> Use <strong className="text-foreground">Evento</strong> quando há horário e local específicos (audiências, reuniões).
            Use <strong className="text-foreground">Tarefa</strong> para prazos e entregas sem horário fixo.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          onClick={handleContinue}
          disabled={!selectedType}
        >
          Continuar
          <ChevronRight className="size-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  )
}
